import { GenericModal } from "components/modals/GenericModal";
import { ModalProps } from "components/types/ModalProps";
import { FC, FormEvent, useMemo, useState } from "react";
import { accountLiterals } from "../literals";
import Input from "components/modals/shared/Input";
import { ICurrency } from "logic/types/currency/ICurrency";
import CtaSecondary from "components/CtaSecondary";
import CtaPrimary from "components/CtaPrimary";
import { useTokenBalances, useUpdateTokenBalances } from "../AccountProvider";
import { AddressInput } from "../AddressInput";
import {
  Option,
  SelectionDropdown,
} from "components/modals/shared/selection-dropdown/SelectionDropdown";
import currenciesModule from "logic/CurrenciesModule";
import { TokenDropdownElement } from "components/modals/shared/selection-dropdown";
import { useUpdateEffect } from "react-use";
import { WalletType } from "contracts/types";
import { Address } from "@unlockdfinance/verislabs-web3";
import useAlertNotification from "components/hooks/useAlertNotification";
import LoadingSpinner from "components/LoadingSpinner";
import {
  useAddressSelectionLogic,
  useAmountSelection,
  useErrorAlert,
} from "logic/hooks";
import classNames from "classnames";
import InputErrorMessage from "components/modals/shared/InputErrorMessage";
import { handleErrors } from "errors";
import { lifeCycleLiterals } from "literals";
import track from "logic/track";

type Props = ModalProps & {
  defaultTokenSelected: ICurrency;
  onTransactionDone: () => void;
};

const { backCta, subheader, submitCta, title, loadingSubmitCta } =
  accountLiterals.sendTokensModal;

const { onSignPending, onSuccess, onTxPending } = lifeCycleLiterals.sendTokens;

const TOKENS = currenciesModule.tokens;

export const ModalSendTokens: FC<Props> = (props) => {
  const [error, setError] = useState<Error | null>(null);
  useErrorAlert(error);
  const { isOpen, toggleModal, defaultTokenSelected, onTransactionDone } =
    props;
  const [tokenSelected, setTokenSelected] =
    useState<ICurrency>(defaultTokenSelected);
  const tokenBalances = useTokenBalances();
  const updateBalances = useUpdateTokenBalances();
  const {
    addressInputValue: addressToSend,
    addressError,
    handleAddressChange: handleAddressToSendChange,
    validateInputAddress,
  } = useAddressSelectionLogic();
  const max = useMemo<bigint>(
    () => tokenBalances.get(tokenSelected.name)!,
    [tokenBalances, tokenSelected]
  );
  const {
    amount,
    resetAmount,
    handleAmountChange,
    validateAmount,
    amountError,
  } = useAmountSelection(max);
  const [, openAlertNotification] = useAlertNotification();
  const [isLoading, setLoading] = useState<boolean>(false);
  const ctaPrimaryLiteral = useMemo<string>(
    () => (isLoading ? loadingSubmitCta : submitCta),
    [isLoading]
  );

  const tokenOptions = useMemo<Option[]>(() => {
    const tokenSelectedName = TOKENS.find(
      (token) => token.name === tokenSelected.name
    )!.name;

    return TOKENS.map((token) => ({
      value: token.name,
      Content: (
        <TokenDropdownElement
          currency={token}
          balance={tokenBalances.get(token.name)!}
        />
      ),
      selected: token.name === tokenSelectedName,
      handleClick: () => handleChangeTokenSelected(token),
    }));
  }, [tokenBalances, tokenSelected]);

  useUpdateEffect(() => {
    resetAmount();
  }, [tokenSelected]);

  const handleChangeTokenSelected = (token: ICurrency) => {
    setTokenSelected(token);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    try {
      const addressError = validateInputAddress();
      const amountError = validateAmount();

      if (!addressError && !amountError && !isLoading) {
        const address = addressToSend as Address;

        await tokenSelected.transfer(WalletType.SMART, address, amount!, {
          onSignaturePending: () => {
            setLoading(true);
            openAlertNotification("info", onSignPending, 500000);
          },
          onLoading: () => {
            openAlertNotification("info", onTxPending, 500000);
          },
        });

        track.click(`account_tokens_${tokenSelected.name}_send_click_send`)

        openAlertNotification("success", onSuccess, 5000);

        updateBalances();

        onTransactionDone();

        toggleModal(false);
      }
    } catch (err) {
      setError(handleErrors(err, "sendTokens"));
      setLoading(false);
    }
  };

  return (
    <GenericModal
      className="w-fit pt-[30px] pr-[30px] pb-[30px] pl-[30px]"
      isOpen={isOpen}
      toggleModal={toggleModal}
      headerTitle={title}
      headerSubtitle={<div className="w-[512px]">{subheader}</div>}
      onSubmit={handleSubmit}
    >
      {isLoading ? (
        <div className="mt-[30px] h-[292px] w-[402px] flex justify-center items-center">
          <LoadingSpinner className="w-10 h-10" />
        </div>
      ) : (
        <div className="mt-[30px] w-[402px] flex flex-col">
          <div className="w-full flex flex-col">
            <AddressInput
              className={classNames(
                "w-full h-[87px] mt-[5px]",
                addressError && "!border-myred"
              )}
              defaultValue={addressToSend}
              onChange={handleAddressToSendChange}
            />
            <div className="w-full mt-1 h-5">
              {addressError && <InputErrorMessage error={addressError} />}
            </div>
          </div>
          <SelectionDropdown
            className="w-full !rounded-[15px] !border"
            heightClassName="h-[87px]"
            options={tokenOptions}
            title="Asset to send"
          />
          <div className="w-full flex flex-col">
            <Input
              className={classNames(
                "mt-6 !border !rounded-[15px]",
                amountError && "!border-myred"
              )}
              nameInput="Amount"
              currency={tokenSelected}
              max={max}
              onChange={handleAmountChange}
              defaultValue={amount}
            />
            <div className="w-full mt-1 h-5">
              {amountError && <InputErrorMessage error={amountError} />}
            </div>
          </div>
        </div>
      )}
      <div className="mt-[30px] w-full flex justify-center gap-[15px]">
        <CtaSecondary
          className="w-[130px]"
          type="button"
          onClick={() => toggleModal()}
        >
          {backCta}
        </CtaSecondary>
        <CtaPrimary className="w-[130px]" type="submit" disabled={isLoading}>
          {ctaPrimaryLiteral}
        </CtaPrimary>
      </div>
    </GenericModal>
  );
};
